import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { createNotification } from "../utils/notifications";
import { getBlockedUserIds } from "../utils/blocks";
import LazyImage from "./LazyImage";
import Toast from "./Toast";
import { IoClose } from "react-icons/io5";
import { useFocusTrap } from "../utils/focusTrap";
import VerifiedBadge from "./VerifiedBadge";

const PAGE_SIZE = 10;

export default function FollowListModal({ userId, type, onClose }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [followingIds, setFollowingIds] = useState(new Set());

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [toast, setToast] = useState("");
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);
  const pendingFollowsRef = useRef(new Set());
  const blockedIdsRef = useRef(new Set());
  const loadingMoreRef = useRef(false);
  const sheetRef = useRef(null);
  useFocusTrap(sheetRef, true);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, type]);

  // Lock background scroll while the sheet is open, close on Escape
  useEffect(() => {
    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  // Fetches one page of this list via a SECURITY DEFINER RPC rather than
  // querying `follows`/`users` directly — the "Show followers"/"Show
  // following" privacy toggle (Profile.jsx settings) used to only be
  // enforced here in the UI (deciding whether the stat was clickable at
  // all), with nothing stopping the exact same direct query from being run
  // from devtools to see a "private" list anyway. get_follow_list() checks
  // the target's flag server-side and returns nothing when it's off (unless
  // the viewer IS the target), so that's no longer possible. A popular
  // account can have tens of thousands of followers, so this stays
  // offset/limit-bounded instead of pulling the whole relationship at once.
  async function fetchPage(start, end) {
    const { data, error } = await supabase.rpc("get_follow_list", {
      p_target_user_id: userId,
      p_type: type,
      p_offset: start,
      p_limit: end - start + 1
    });

    if (error) {
      console.log("LOAD FOLLOW LIST ERROR:", error);
      return { list: [], ids: [] };
    }

    const list = data || [];
    return { list, ids: list.map(u => u.id) };
  }

  // A block only removes the blocker's own follow row (see utils/blocks.js)
  // — the other direction is left in the table on purpose, since every
  // other surface (feeds) hides blocked users via this same id set instead
  // of deleting data. This list has to apply that same filter itself,
  // otherwise someone you blocked still shows up in your own followers
  // list with a working profile link and Follow button.
  function excludeBlocked(list, ids) {
    const blocked = blockedIdsRef.current;
    if (blocked.size === 0) return { list, ids };
    return {
      list: list.filter(u => !blocked.has(u.id)),
      ids: ids.filter(id => !blocked.has(id))
    };
  }

  // For "is the viewer already following this row" state, only check the
  // users on THIS page — not the viewer's entire following list (which,
  // for someone who follows a lot of people, would be its own unbounded read).
  async function markFollowingState(me, ids) {
    if (!me || ids.length === 0) return;

    const { data } = await supabase
      .from("follows")
      .select("following_id")
      .eq("follower_id", me)
      .in("following_id", ids);

    setFollowingIds(prev => new Set([...prev, ...(data || []).map(r => r.following_id)]));
  }

  async function loadList() {
    setLoading(true);
    setHasMore(true);

    const {
      data: { session }
    } = await supabase.auth.getSession();
    const me = session?.user ?? null;
    setCurrentUserId(me?.id || null);
    blockedIdsRef.current = new Set(await getBlockedUserIds(me?.id));

    const raw = await fetchPage(0, PAGE_SIZE - 1);
    // hasMore reflects whether the DB page was full, before filtering —
    // otherwise blocking a few people on a page could look like "no more
    // results" and cut the list short.
    const hasMoreNext = raw.list.length === PAGE_SIZE;
    const { list, ids } = excludeBlocked(raw.list, raw.ids);
    await markFollowingState(me?.id, ids);

    setRows(list);
    setOffset(PAGE_SIZE);
    setHasMore(hasMoreNext);
    setLoading(false);
  }

  async function loadMore() {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const raw = await fetchPage(offset, offset + PAGE_SIZE - 1);
    const hasMoreNext = raw.list.length === PAGE_SIZE;
    const { list, ids } = excludeBlocked(raw.list, raw.ids);
    await markFollowingState(currentUserId, ids);

    setRows(prev => [...prev, ...list]);
    setOffset(prev => prev + PAGE_SIZE);
    setHasMore(hasMoreNext);
    loadingMoreRef.current = false;
    setLoadingMore(false);
  }

  // Infinite scroll: the list scrolls inside the sheet itself, not the
  // page, so the observer's root has to be that scrollable body element.
  useEffect(() => {
    const el = sentinelRef.current;
    const root = bodyRef.current;
    if (!el || !root || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, offset, loadingMore]);

  async function toggleFollow(targetId) {
    if (!currentUserId) {
      onClose?.();
      navigate("/login");
      return;
    }

    // Without this, a fast double-tap fires two requests before the first
    // one's result has re-rendered the button — both read the same starting
    // followingIds and both send the same INSERT (or DELETE). Since the
    // follower/following counts shown elsewhere are a live COUNT(*) on this
    // table (see PublicProfile.jsx), a duplicate row from that race doesn't
    // just glitch this button, it permanently inflates a public count.
    if (pendingFollowsRef.current.has(targetId)) return;
    pendingFollowsRef.current.add(targetId);

    const nowFollowing = followingIds.has(targetId);

    // Optimistic — but reverted below if the write actually fails (e.g. a
    // blocked-user insert rejected by RLS), so the button can't get stuck
    // showing "Following" for a follow that was never actually created.
    setFollowingIds(prev => {
      const next = new Set(prev);
      if (nowFollowing) next.delete(targetId);
      else next.add(targetId);
      return next;
    });

    function revert() {
      setFollowingIds(prev => {
        const next = new Set(prev);
        if (nowFollowing) next.add(targetId);
        else next.delete(targetId);
        return next;
      });
    }

    try {
      if (nowFollowing) {
        const { error } = await supabase
          .from("follows")
          .delete()
          .eq("follower_id", currentUserId)
          .eq("following_id", targetId);

        if (error) {
          console.log("UNFOLLOW ERROR:", error);
          revert();
          setToast("Couldn't unfollow. Try again.");
        }
      } else {
        const { error } = await supabase.from("follows").insert({
          follower_id: currentUserId,
          following_id: targetId
        });

        if (error) {
          console.log("FOLLOW ERROR:", error);
          revert();
          setToast("Couldn't follow. Try again.");
          return;
        }

        createNotification({ userId: targetId, actorId: currentUserId, type: "follow" });
      }
    } finally {
      pendingFollowsRef.current.delete(targetId);
    }
  }

  function goToProfile(id) {
    onClose?.();
    if (id === currentUserId) navigate("/profile");
    else navigate(`/u/${id}`);
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose?.();
  }

  const title = type === "followers" ? "Followers" : "Following";

  return createPortal(
    <div className="followlist-backdrop" onClick={handleBackdropClick}>
      <div className="followlist-sheet" ref={sheetRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}>
        <style>{`
          .followlist-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(20, 14, 8, 0.55);
            z-index: 1000;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          }

          .followlist-sheet {
            background: var(--bg-surface);
            width: 100%;
            max-height: 78vh;
            border-radius: 18px 18px 0 0;
            display: flex;
            flex-direction: column;
            box-shadow: 0 -4px 24px rgba(0,0,0,0.2);
            animation: followlist-slide-up 0.22s ease-out;
          }

          @keyframes followlist-slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .followlist-handle {
            width: 40px;
            height: 4px;
            border-radius: 2px;
            background: var(--border-input);
            margin: 10px auto 0;
            flex-shrink: 0;
          }

          .followlist-header {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            padding: 12px 16px;
            border-bottom: 1px solid var(--border-soft);
            flex-shrink: 0;
          }

          .followlist-close {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            background: none;
            border: none;
            font-size: 22px;
            line-height: 1;
            cursor: pointer;
            color: var(--text-body);
            padding: 4px;
          }

          .followlist-body {
            flex: 1;
            overflow-y: auto;
            padding: 4px 16px;
            -webkit-overflow-scrolling: touch;
          }

          @media (min-width: 641px) {
            .followlist-backdrop {
              align-items: center;
            }
            .followlist-sheet {
              width: 92%;
              max-width: 420px;
              max-height: 78vh;
              border-radius: 16px;
              animation: followlist-pop-in 0.18s ease-out;
            }
            .followlist-handle {
              display: none;
            }
            @keyframes followlist-pop-in {
              from { transform: scale(0.96); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
          }
        `}</style>

        <div className="followlist-handle" />
        <div className="followlist-header">
          <strong style={{ fontSize: 15 }}>{title}</strong>
          <button className="followlist-close" onClick={onClose} aria-label="Close">
            <IoClose />
          </button>
        </div>

        <div className="followlist-body" ref={bodyRef}>
          {loading ? (
            <p style={mutedText}>Loading…</p>
          ) : rows.length === 0 ? (
            <p style={mutedText}>
              {type === "followers" ? "No followers yet." : "Not following anyone yet."}
            </p>
          ) : (
            rows.map(u => {
              const isMe = u.id === currentUserId;
              const isFollowing = followingIds.has(u.id);

              return (
                <div key={u.id} style={row}>
                  <div style={rowLeft} onClick={() => goToProfile(u.id)}>
                    <LazyImage
                      src={u.avatar_url || "/default-avatar.svg"}
                      alt={u.username}
                      style={rowAvatar}
                    />
                    <span style={rowUsername}>
                      @{u.username}
                      {u.verified && <VerifiedBadge size={16} style={{ verticalAlign: -1 }} />}
                    </span>
                  </div>

                  {!isMe && (
                    <button
                      onClick={() => toggleFollow(u.id)}
                      style={isFollowing ? unfollowBtn : followBtn}
                    >
                      {isFollowing ? "Following" : "Follow"}
                    </button>
                  )}
                </div>
              );
            })
          )}

          {!loading && hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
          {loadingMore && <p style={mutedText}>Loading more…</p>}
        </div>
      </div>

      {toast && <Toast text={toast} onClose={() => setToast("")} />}
    </div>,
    document.body
  );
}

/* ===== STYLES (Virsa palette, matches Comments) ===== */

const row = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "10px 0",
  borderBottom: "1px solid var(--border-soft)"
};

const rowLeft = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  minWidth: 0
};

const rowAvatar = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)",
  flexShrink: 0
};

const rowUsername = {
  fontWeight: 700,
  fontSize: 14,
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

const followBtn = {
  flexShrink: 0,
  padding: "6px 16px",
  borderRadius: 20,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 12.5,
  cursor: "pointer"
};

const unfollowBtn = {
  ...followBtn,
  background: "var(--bg-surface)",
  color: "var(--text-secondary)",
  border: "1.5px solid var(--border-input)"
};

const mutedText = { fontSize: 13, color: "var(--text-muted)", margin: "8px 0" };
